import { ReactNode, useMemo } from 'react'
import { useSelector } from 'react-redux'

import { useEnvironmentURLs } from '@/atoms'
import { selectUserConfiguration } from '@/features/domain/user'
import { conversionUtils } from '@/server-data'

import Close from '@mui/icons-material/Close'
import { Stack } from '@mui/material'
import { ActionBarItem } from '@workwave-tidal/core/components/ActionBar'
import { FilePreview, FilePreviewThumbnail } from '@workwave-tidal/lab'

import { preview } from '@/components/form/preview'
import { PodListItemField } from '../fields/PodListItemField'

import { form } from '../../../formState'
import * as OrderTexts from '../../../intl'

import type { ExecutionEventsFormValues } from '../../../typings'
import type { OnRemovePOD, Props } from '../typings'

type PodPictureProps = {
  onRemove: OnRemovePOD
  picture: uui.domain.client.rm.MediaPod
  index: number
  userConfig: uui.domain.UserConfiguration
} & {
  onTogglePod: Props['onTogglePod']
  activePodMarker: Props['activePodMarker']
}

function PodPicture(props: PodPictureProps) {
  const { picture, onRemove, index, userConfig } = props
  const { sec, token } = picture

  const { podsBaseUrl } = useEnvironmentURLs()

  const time = conversionUtils.convertTime(userConfig)(sec)
  const title = `${OrderTexts.getPictureLabel()} ${index + 1}`
  const src = `${podsBaseUrl}${token}`

  const actions = [
    <ActionBarItem
      icon={<Close />}
      id="close"
      key="close"
      label={OrderTexts.getCloseButtonLabel()}
      onClick={() => onRemove(picture.uuid)}
    />,
  ]

  return (
    <FilePreview
      actions={actions}
      actionsVisibility="always"
      icon={<FilePreviewThumbnail objectFit="fill" src={src} />}
      subtitle={time}
      title={title}
      tooltip={title}
      variant="standard"
      testId={`pod_picture_${index}`}
    />
  )
}

export function PodPictures(props: Props) {
  const { activePodMarker, onTogglePod, values: initialValue } = props
  const userConfig = useSelector(selectUserConfiguration)

  const renderItems = useMemo(
    () =>
      (values: uui.domain.client.rm.Pod[], onRemove: OnRemovePOD): ReactNode => {
        const sortedValues = [...values].sort((a, b) => a.sec - b.sec)

        return (
          <Stack spacing={2}>
            {sortedValues.length > 0 &&
              sortedValues.map((picture, index) => (
                <PodPicture
                  picture={picture as uui.domain.client.rm.MediaPod}
                  index={index}
                  activePodMarker={activePodMarker}
                  onTogglePod={onTogglePod}
                  userConfig={userConfig}
                  onRemove={onRemove}
                  key={picture.uuid}
                />
              ))}
            {values.length === 0 && <preview.Value>{OrderTexts.getNoneText()}</preview.Value>}
          </Stack>
        )
      },
    [activePodMarker, onTogglePod, userConfig],
  )

  return (
    <>
      {initialValue.formPods.pictures.length > 0 && (
        <Stack spacing={1}>
          <form.SectionSubtitle>{OrderTexts.getPicturesLabel()}</form.SectionSubtitle>

          <PodListItemField<'formPods.pictures', ExecutionEventsFormValues>
            name="formPods.pictures"
            podListItemFieldInputProps={{
              testId: 'pod-pictures',
              renderItems,
            }}
          />
        </Stack>
      )}
    </>
  )
}
